import { ComponentRef, Injectable, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { AbcCore, CollectionHelper, UrlHelper } from '@barthauer/abc/core';
import { CacheService, CacheStorageAdapter, ICoreDataEntity, ProgressState } from '@barthauer/abc/core/data';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { DataEntity, MapService, MapTarget, MapView } from '@barthauer/abc/graphics/adapters/open-layers';
import { UserSettingsComponent } from '@barthauer/abc/management/users';
import { DialogResult, MessageBoxService, MessageMode, MessageSettings } from '@barthauer/abc/messages';
import { AbcContainerService, AbcWindowComponent, IWindow, WindowStyle } from '@barthauer/abc/ui-components';
import { GisExportComponent, GisExportService } from '@barthauer/basys/interfaces/common/gis/wizards/export';
import { M150ExportComponent, M150ExportService } from '@barthauer/basys/interfaces/wastewater/m150/wizards/export';
import { NodeGraphicsComponent, PipeGraphicsComponent } from '@barthauer/basys/graphics/wastewater';
import { ArenaService } from 'libs/abc/arena/src/lib/core/arena.service';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { WizardImportComponent } from 'libs/abc/interfaces/core/wizards/import/src/lib/wizard-import.component';
import { AsciiImportComponent } from 'libs/basys/interfaces/common/ascii/wizards/import/src/lib/ascii-import.component';
import { AsciiImportService } from 'libs/basys/interfaces/common/ascii/wizards/import/src/lib/ascii-import.service';
import { from, Observable, of, Subject } from 'rxjs';
import { concatAll, concatMap, map, take, toArray } from 'rxjs/operators';
import { BasysMapFrameService } from '../basys-map-frame.service';
import { CacheConfigComponent } from '../caching/cache-config/cache-config.component';
import { AddObjectComponent } from '../manipulation/add-object/add-object.component';
import { SelectOtAndGidComponent } from '../manipulation/select-ot-and-gid/select-ot-and-gid.component';
import { WidgetConfiguration } from '../models/config.model';
import { CreateGeoObjectInfo, ObjectTypeWithGeoDefsCandidate, SelectOtAndGidListItem } from '../models/mapsData.model';
import { BooleanSetting } from '../models/setting.model';
import { ViewStateChangeArg, ViewStateChangeArgType } from '../models/viewState.model';
import { ObjectInfoComponent } from '../object-info/object-info/object-info.component';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { LongitudinalSectionComponent } from '@barthauer/basys/graphics/common';

@Injectable({
  providedIn: 'root',
})
export class LibService {
  private objectInfoDataSubject = new Subject<DataEntity[]>();
  private documentsDataSubject = new Subject<DataEntity[]>();
  private addObjectDataSubject = new Subject<DataEntity>();
  private viewStateChangedSubject = new Subject<ViewStateChangeArg>();
  private closeBottomSheetSubject = new Subject<void>();
  private openBottomSheetSubject = new Subject<WidgetConfiguration>();
  private isClickFromTableSubject = new Subject<boolean>();
  private windowInstance: AbcWindowComponent;
  private nodeGraphicsWindowInstance: AbcWindowComponent = null;
  private pipeGraphicsWindowInstance: AbcWindowComponent = null;
  private longitudinalSectionWindowInstance: AbcWindowComponent = null;
  private selectOtAndGIdWindowInstance: AbcWindowComponent;
  private selectOtAndGidComponentRef: ComponentRef<any>;
  public arenaLayoutCaption: Subject<string> = new Subject();

  private newObjectWindowInstance: AbcWindowComponent;
  private newObjectComponentRef: ComponentRef<any>;
  private newObjectComponentInstance: AddObjectComponent;

  public frame: BasysMapFrameService;
  public reloadGrid: Subject<boolean> = new Subject();
  public mapInfoStateStorageKey: Subject<string> = new Subject();
  public mapInfoStateStorageKey$ = this.mapInfoStateStorageKey.asObservable();
  currentBottomSheetPanelHeight?: number;
  public resAsync: Promise<ResourceSet>;
  private stateStorageKey: string;
  private width: string;
  private height: string;
  private minHeight: string;
  private minWidth: string;
  private position: object;
  private windowHeight: number;
  private windowWidth: number;
  private targetWindows = new Map<string, Window>();
  wizardComponentRef: ComponentRef<any>;
  @ViewChild('abcWindowContainer', { read: ViewContainerRef })
  abcWindow: ViewContainerRef;

  public isBottomSheetOpenByName: (name: string) => boolean = (_) => false;
  public preferMtObjectDocuments: boolean = false;

  constructor(
    private abcContainer: AbcContainerService,
    private resourceService: ResourceManagerService,
    private cacheService: CacheService,
    private messageBoxService: MessageBoxService,
    private abcCore: AbcCore,
    private mapService: MapService,
    private m150ExportService: M150ExportService,
    private gisExportService: GisExportService,
    private arenaService: ArenaService,
    private asciiImportService: AsciiImportService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('basys-map-frame');
    this.mapInfoStateStorageKey$.subscribe((data) => {
      this.stateStorageKey = data;
    });
  }

  private getWindowParameter(stateStorageKey) {
    if (stateStorageKey) {
      const getStateStorageKeyFromLocalStorage = localStorage.getItem(stateStorageKey);
      if (getStateStorageKeyFromLocalStorage) {
        const stateStorageKeyObject = JSON.parse(getStateStorageKeyFromLocalStorage);
        this.position = stateStorageKeyObject[0];
        this.width = stateStorageKeyObject[1].width;
        this.height = stateStorageKeyObject[1].height;
        let offsetTop: number;
        let offsetLeft: number;

        if (this.height !== 'auto') {
          this.minHeight = stateStorageKeyObject[1].height;
          offsetTop = stateStorageKeyObject[1].height / 2 + stateStorageKeyObject[0].offset['y'] - 25;
        } else {
          offsetTop = this.windowHeight / 2 + stateStorageKeyObject[0].offset['y'] - 25;
        }
        if (this.width !== 'auto') {
          this.minWidth = stateStorageKeyObject[1].width;
          offsetLeft = stateStorageKeyObject[1].width / 2 + stateStorageKeyObject[0].offset['x'] - 25;
        } else {
          offsetLeft = this.windowWidth / 2 + stateStorageKeyObject[0].offset['x'] - 25;
        }

        setTimeout(() => {
          document
            .getElementById('spinner-container')
            ?.setAttribute('style', 'top:' + offsetTop + 'px;left:' + offsetLeft + 'px;margin:0');
        }, 0);
      }
    }
  }

  closeBottomSheet() {
    this.closeBottomSheetSubject.next(null);
  }
  openBottomSheetByName(widget: string) {
    this.openBottomSheet(this.frame.getWidget(widget));
  }
  openBottomSheet(widget: WidgetConfiguration) {
    this.openBottomSheetSubject.next(widget);
  }

  showObjectInfo(objectInfos: DataEntity[]) {
    if (
      !(this.frame.frameConfig.settings.get(BasysMapFrameService.Setting_ObjectInfoAsPopup) as BooleanSetting).value
    ) {
      if (this.isBottomSheetOpenByName && !this.isBottomSheetOpenByName(BasysMapFrameService.Widget_ObjectInfo)) {
        const sub = this.onViewStateChanged().subscribe((state) => {
          if (state.type === ViewStateChangeArgType.BottomSheetPanelOpening) {
            sub.unsubscribe();
            this.sendDataForObjectInfo(objectInfos);
          }
        });
        this.openBottomSheetByName(BasysMapFrameService.Widget_ObjectInfo);
      } else {
        this.sendDataForObjectInfo(objectInfos);
      }
    } else {
      this.showPopupObjectInfoComponent(() => {
        this.sendDataForObjectInfo(objectInfos);
      });
    }
  }

  private isObjectsSelectedMessageBound: boolean = false;
  async openTargetUrl(
    layerEntities: DataEntity[],
    target?: MapTarget,
    propertyGetter?: (entity: DataEntity, propertyName: string) => Promise<any>,
  ): Promise<Window> {
    if (layerEntities) {
      for (const entity of layerEntities) {
        if (!target) {
          target = entity.feature?.layerdefinition?.target;
        }
        let url: string = target?.url;
        if (url) {
          if (propertyGetter) {
            let vw: MapView;
            if (this.mapService.getCurrentView) {
              vw = this.mapService.getCurrentView();
            }
            if (vw) {
              url = await vw.replaceTargetUrlValues(url, async (name) => {
                let value = await propertyGetter(entity, name);
                if (!value && name === 'id') {
                  value = entity.id;
                } else if (!value && name === 'objecttype') {
                  value = entity.objectType;
                } else if (!value && name === 'keyextern') {
                  value = entity.feature?.get('KeyExtern');
                }
                return value;
              });
            }
          }
          const parts = url.split('|');
          if (target?.name && parts.length > 1) {
            const wnd = this.targetWindows.get(target.name);
            if (wnd && !wnd.closed && parts[1]) {
              wnd.postMessage({ name: 'search', arg: parts[1] }, '*');
              return wnd;
            }
          }
          let windowFeatures: string;
          if (parts.length > 1) {
            url = parts[0] + parts[1];
          }
          if (parts.length > 2) {
            windowFeatures = parts[2];
          }
          if (!this.isObjectsSelectedMessageBound && url.indexOf('hignabuttons=true') >= 0) {
            const func = (ev) => {
              if (ev.data.name !== 'objectsSelected') {
                return;
              }
              const entities: ICoreDataEntity[] = ev.data.entities;
              if (entities?.length) {
                this.mapService.navigateToObjects(entities.map((_) => new DataEntity(_.ObjectType, _.Id)));
              }
            };

            if (window.addEventListener) {
              window.addEventListener('message', func.bind(this), false);
            } else {
              (<any>window).attachEvent('onmessage', func.bind(this));
            }
            this.isObjectsSelectedMessageBound = true;
          }
          return this.openUrl(url, target?.name, windowFeatures);
        }
      }
    }
  }

  public openUrl(url: string, target: string, windowFeatures?: string): Window {
    if (url) {
      const finalTarget = target || '_blank';
      const wnd = this.mapService.getCurrentView().openUrl(this.abcCore, url, finalTarget, windowFeatures);
      if (wnd && target) {
        this.targetWindows.set(finalTarget, wnd);
      }
      return wnd;
    }
  }

  private getWindowWidth(width: number): number {
    return document.body.offsetWidth > width ? width : document.body.offsetWidth;
  }

  private isSmallResolution(): boolean {
    return document.body.offsetWidth <= 640 ? true : false;
  }

  public async openSelectObjectTypeAndGeoDefDialog(
    objectTypeAndGeoDefIdListItems: SelectOtAndGidListItem[],
    continueIfOk: (info: CreateGeoObjectInfo) => void,
    continueIfCancelled: () => void = null,
  ) {
    const style: WindowStyle = {
      height: objectTypeAndGeoDefIdListItems.length < 3 ? '300' : '400',
      width: '350',
      minHeight: '200',
      minWidth: '200',
      shading: true,
      resizable: true,
      position: { my: 'center center', at: 'center center', offset: '0 0' },
    };

    const windowAttr: IWindow = {
      id: 'selectObjectTypeAndGeoDefDialog',
      label: (await this.resAsync).get('SelectOtAndGid-WindowTitle'),
      maximizeButton: false,
      minimizeButton: false,
      component: SelectOtAndGidComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };

    let abcWindow: ViewContainerRef;
    const window = this.abcContainer.createComponent(abcWindow, AbcWindowComponent, windowAttr);
    this.selectOtAndGidComponentRef = window.componentRef;
    this.selectOtAndGIdWindowInstance = <AbcWindowComponent>window.componentRef.instance;

    this.arenaLayoutCaption.subscribe((caption) => {
      if (this.selectOtAndGIdWindowInstance) {
        this.selectOtAndGIdWindowInstance.label = caption;
        this.selectOtAndGIdWindowInstance.showLoader = false;
      }
    });

    this.selectOtAndGIdWindowInstance.createComponentInstance().subscribe((activeComponent: any) => {
      const w = activeComponent.openWindow();
      const dialogInstance = w.instance;
      const typedDialogInstance = <SelectOtAndGidComponent>dialogInstance;
      typedDialogInstance?.initialize(objectTypeAndGeoDefIdListItems);

      this.selectOtAndGIdWindowInstance.onAfterOpened.subscribe(() =>
        this.onSelectOtAndGidWindowOpened(typedDialogInstance, continueIfOk, continueIfCancelled),
      );

      this.selectOtAndGIdWindowInstance.onAfterClosed.subscribe(() => this.onSelectOtAndGidWindowClosed());
    });
  }

  private selectOtAndGidComponentInstance: SelectOtAndGidComponent;

  private onSelectOtAndGidWindowOpened(
    initializedDialogInstance: SelectOtAndGidComponent,
    continueIfOk: (info: CreateGeoObjectInfo) => void,
    continueIfCancelled: () => void = null,
  ) {
    this.selectOtAndGidComponentInstance = initializedDialogInstance;
    this.selectOtAndGidComponentInstance?.closeDialog.subscribe((info) =>
      this.onSelectOtAndGidComponentClosed(info, continueIfOk, continueIfCancelled),
    );
  }

  private onSelectOtAndGidWindowClosed() {
    if (this.selectOtAndGidComponentRef) {
      this.selectOtAndGidComponentRef.destroy();
      this.selectOtAndGIdWindowInstance = null;
    }
  }

  private onSelectOtAndGidComponentClosed(
    info: CreateGeoObjectInfo,
    continueIfOk: (info: CreateGeoObjectInfo) => void,
    continueIfCancelled: () => void = null,
  ) {
    if (this.selectOtAndGIdWindowInstance) {
      this.selectOtAndGIdWindowInstance.closeDialog();
    }

    if (info && info.objectTypeName && info.geoIdentifier) {
      continueIfOk(info);
    } else if (continueIfCancelled) {
      continueIfCancelled();
    }
  }

  public ensureCloseObjectTypeAndGeoDefDialog() {
    if (this.selectOtAndGIdWindowInstance) {
      this.selectOtAndGIdWindowInstance.closeDialog();

      if (this.selectOtAndGidComponentRef) {
        this.selectOtAndGidComponentRef.destroy();
      }
    }
  }

  public async buildSelectObjectTypeAndGeoDefIdList(
    mapInfoObjectTypeCandidates: ObjectTypeWithGeoDefsCandidate[],
  ): Promise<SelectOtAndGidListItem[]> {
    const buildSelectOtAndGidListObservable$: Observable<SelectOtAndGidListItem[]> =
      mapInfoObjectTypeCandidates && mapInfoObjectTypeCandidates.length > 0
        ? from(mapInfoObjectTypeCandidates.sort((a, b) => a.objectType.name.localeCompare(b.objectType.name))).pipe(
            concatMap((c) => this.resolveMapInfoObjectType(c)),
            concatAll(),
            toArray(),
            map((items) =>
              (SelectOtAndGidComponent.AVOID_DUPLICATE_LIST_ENTRIES ? this.mergeListItemDuplicates(items) : items).sort(
                (a, b) => a.concreteObjectTypeName.localeCompare(b.concreteObjectTypeName),
              ),
            ),
          )
        : of(<SelectOtAndGidListItem[]>[]);

    // Ensure completion/unsubscribe directly after first emit (not after complete())
    return buildSelectOtAndGidListObservable$.pipe(take(1)).toPromise(); // mimic firstValueFrom() behavior in RxJS <7
    //With RxJS >=7 toPromise() is deprecated. Instead: return firstValueFrom(buildSelectOtAndGidListObservable);
  }

  private resolveMapInfoObjectType(mapInfoOt: ObjectTypeWithGeoDefsCandidate): Observable<SelectOtAndGidListItem[]> {
    return this.abcCore.objectSpace.getObjectType(mapInfoOt.objectType.name).pipe(
      map((ot1) =>
        [ot1]
          .concat(ot1.allDerivedTypes)
          .filter((ot2) => ot2.value > 0) // only concrete OTs
          .map(
            (ot3) =>
              new SelectOtAndGidListItem({
                concreteObjectTypeName: ot3.name,
                concreteObjectTypeDisplayTextSingular: ot3.displayText,
                groupObjectTypeName: ot1.allDerivedTypes.length === 0 ? null : ot1.name,
                groupObjectTypeDisplayTextPlural: ot1.allDerivedTypes.length === 0 ? null : ot1.displayTextPlural,
                geoIdentifiers: mapInfoOt.allowedGeoDefinitions.map((geoDef) => geoDef.identifier).sort(),
              }),
          ),
      ),
    );
  }

  private mergeListItemDuplicates(dataItems: SelectOtAndGidListItem[]): SelectOtAndGidListItem[] {
    return CollectionHelper.groupBy(dataItems, (item) => item.concreteObjectTypeName).map((group) => {
      const mergedItem = new SelectOtAndGidListItem(group[0]);
      mergedItem.geoIdentifiers = CollectionHelper.distinctValues(
        CollectionHelper.mapAndFlatten(group.items, (g) => g.geoIdentifiers),
      ).sort();

      return mergedItem;
    });
  }

  private createObjectInfoWindow<T>(
    component: T,
    id: string,
    title: string,
  ): {
    componentRef: ComponentRef<unknown>;
    domElement: HTMLElement;
  } {
    this.width = this.isSmallResolution() ? '100%' : 'auto';
    this.height = this.isSmallResolution() ? '80%' : 'auto';
    this.minHeight = '320px';
    this.position = this.isSmallResolution() ? { my: 'left top', at: 'left top' } : { my: 'center', at: 'center' };
    this.getWindowParameter(this.isSmallResolution() ? null : this.stateStorageKey);
    const style: WindowStyle = {
      height: this.height,
      width: this.width,
      minHeight: this.minHeight,
      minWidth: '320px',
      maxHeight: this.isSmallResolution() ? '90%' : '100%',
      maxWidth: this.getWindowWidth(500) + 'px',
      position: this.position,
      resizable: true,
    };
    const windowAttr: IWindow = {
      id: id,
      label: title,
      showLoader: true,
      storeState: true,
      maximizeButton: true,
      minimizeButton: true,
      minumizeDialogOption: -64,
      component: component,
      isInMobile: true,
      class: 'maps-dataForm-window',
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };
    let abcWindow: ViewContainerRef;
    return this.abcContainer.createComponent(abcWindow, AbcWindowComponent, windowAttr);
  }

  public async openNewObjectDialog(objectInfo: DataEntity, identifier: string) {
    if (this.windowInstance) {
      this.windowInstance.closeDialog();
    }
    if (!this.newObjectComponentInstance) {
      const window = this.createObjectInfoWindow(
        AddObjectComponent,
        'newObjectDialog',
        (await this.resAsync).get('AddObject-WindowTitle'),
      );
      this.newObjectComponentRef = window.componentRef;
      this.newObjectWindowInstance = <AbcWindowComponent>window.componentRef.instance;

      this.newObjectWindowInstance.createComponentInstance().subscribe((activeComponent: any) => {
        const w = activeComponent.openWindow();
        const dialogInstance = w.instance;
        const typedDialogInstance = <AddObjectComponent>dialogInstance;
        typedDialogInstance?.init(objectInfo, identifier);

        this.newObjectWindowInstance.onAfterOpened.subscribe(() => this.onNewObjectWindowOpened(typedDialogInstance));

        this.arenaLayoutCaption.subscribe(() => {
          this.newObjectWindowInstance.showLoader = false;
        });

        this.newObjectWindowInstance.onAfterClosed.subscribe(() => this.onNewObjectWindowClosed());
      });
    }

    const handler = async () => {
      if (CacheStorageAdapter.isOffline || CacheStorageAdapter.forceOfflineMode) {
        this.frame?.closeMainMenu();
        this.newObjectWindowInstance?.closeDialog();
      }
    };

    CacheStorageAdapter.isOfflineChanged.subscribe(async () => await handler());
  }

  private onNewObjectWindowOpened(initializedDialogInstance: AddObjectComponent) {
    this.newObjectComponentInstance = initializedDialogInstance;
    this.newObjectComponentInstance?.closeDialog.subscribe((info) => this.onNewObjectWindowClosed());
  }

  private onNewObjectWindowClosed() {
    if (this.newObjectComponentInstance) {
      this.newObjectComponentRef.destroy();
      this.newObjectComponentInstance = undefined;
    }
  }

  public showPopupObjectInfoComponent(afterLoadedMethod) {
    this.arenaService.navigationNodeClick$.subscribe((x) => {
      this.windowInstance?.closeDialog();
    });

    if (this.newObjectWindowInstance) {
      this.newObjectWindowInstance.closeDialog();
      this.newObjectComponentRef.destroy();
      this.newObjectComponentInstance = undefined;
    }
    if (!this.windowInstance) {
      const window = this.createObjectInfoWindow(ObjectInfoComponent, 'window-object-info', '');
      this.windowInstance = <AbcWindowComponent>window.componentRef.instance;
      this.arenaLayoutCaption.subscribe((caption) => {
        if (this.windowInstance) {
          this.windowInstance.label = caption;
          this.windowInstance.showLoader = false;
        }
      });

      this.windowInstance.createComponentInstance().subscribe((activeComponent: any) => {
        activeComponent.openWindow();
        activeComponent.onAfterOpened.subscribe(() => {
          this.windowInstance.storeStateKey = this.stateStorageKey;
          if (this.isSmallResolution()) {
            const oiHeight = document.getElementsByClassName('dx-scrollview-content')[1].clientHeight;
            if (document.body.offsetHeight * 0.8 > oiHeight) this.windowInstance.attrHeight = 'auto';
          }
          this.windowHeight = document.getElementById('window-object-info')?.parentElement.clientHeight;
          this.windowWidth = document.getElementById('window-object-info').parentElement.clientWidth;
          if (afterLoadedMethod) {
            afterLoadedMethod();
          }
        });
        activeComponent.onAfterClosed.subscribe(() => {
          window.componentRef.destroy();
          this.windowInstance = null;
        });
      });
    } else {
      afterLoadedMethod();
    }
  }

  public ensureClosePopupObjectInfo() {
    if (this.windowInstance) {
      this.windowInstance.closeDialog();
    }
  }

  public async showCacheConfigComponent() {
    const style: WindowStyle = {
      height: '100%',
      minHeight: '320px',
      maxHeight: '555px',
      maxWidth: '600px',
      width: '100%',
      resizable: true,
    };
    const windowAttr: IWindow = {
      id: 'window-cache-config',
      label: (await this.resAsync).get('CacheConfig-Title'),
      maximizeButton: true,
      minimizeButton: true,
      minumizeDialogOption: -64,
      component: CacheConfigComponent,
      isInMobile: true,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };

    let abcWindow: ViewContainerRef;
    const window = this.abcContainer.createComponent(abcWindow, AbcWindowComponent, windowAttr);
    const windowInstance: AbcWindowComponent = <AbcWindowComponent>window.componentRef.instance;
    windowInstance.createComponentInstance().subscribe((activeComponent: any) => {
      const w = activeComponent.openWindow();
      const cachingInstance: CacheConfigComponent = w.instance;
      cachingInstance.frame = this.frame;
      cachingInstance.parentWindow = windowInstance;

      let cancelClosing = true;
      activeComponent.onClosing
        .asObservable()
        .pipe(
          concatMap((event: any) => {
            event.cancel = cancelClosing;
            if (cancelClosing && this.cacheService.progressState !== ProgressState.Idle) {
              this.confirmCachingClosing().subscribe((dlgResult: any) => {
                if (dlgResult.result === DialogResult.Yes) {
                  this.cacheService.stopCaching();
                  cancelClosing = false;
                  activeComponent.closeDialog();
                }
                this.messageBoxService.dialog.closeAll();
              });
              return of(null);
            }
          }),
        )
        .subscribe(() => {});
      activeComponent.onAfterClosed.subscribe(() => {
        window.componentRef.destroy();
      });
    });
  }

  public async openUserSettingsComponent(dataChanged: () => void): Promise<void> {
    const style: WindowStyle = {
      height: '600px',
      width: '500px',
      minHeight: '600px',
      minWidth: '200px',
      maxHeight: '99%',
      maxWidth: '99%',
      resizable: true,
      shading: true,
    };

    const windowAttr: IWindow = {
      id: 'userSettings',
      label: (await this.resAsync).get('UserSettings-WindowTitle'),
      maximizeButton: true,
      minimizeButton: true,
      component: UserSettingsComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };

    let abcWindow: ViewContainerRef;
    const window = this.abcContainer.createComponent(abcWindow, AbcWindowComponent, windowAttr);
    const windowInstance: AbcWindowComponent = <AbcWindowComponent>window.componentRef.instance;
    windowInstance.createComponentInstance().subscribe((windowComponent: any) => {
      const userSettingsInstance: UserSettingsComponent = <UserSettingsComponent>windowComponent.openWindow().instance;
      userSettingsInstance.changedEvent.subscribe(() => dataChanged());

      windowComponent.onContentReady.subscribe((event) => {
        const contentElement = event.component.content();
        contentElement.style.overflow = 'auto';
      });
      windowComponent.onAfterClosed.subscribe(() => window.componentRef.destroy());
    });
  }

  public closeGraphicsPopups(): void {
    this.nodeGraphicsWindowInstance?.closeDialog();
    this.pipeGraphicsWindowInstance?.closeDialog();
    this.longitudinalSectionWindowInstance?.closeDialog();
  }

  public async openPipeGraphicsComponent(entity: DataEntity): Promise<void> {
    if (this.pipeGraphicsComponent) {
      this.pipeGraphicsComponent.entity = entity;
      return;
    }

    await this.openGraphicsComponentCore<PipeGraphicsComponent>(
      PipeGraphicsComponent,
      (_) => (this.pipeGraphicsComponent = _),
      (_) => (this.pipeGraphicsWindowInstance = _),
      () => (this.pipeGraphicsComponent.entity = entity),
      'PipeGraphics',
    );
  }
  private pipeGraphicsComponent: PipeGraphicsComponent = null;

  public async openNodeGraphicsComponent(entity: DataEntity): Promise<void> {
    if (this.nodeGraphicsComponent) {
      this.nodeGraphicsComponent.entity = entity;
      return;
    }

    await this.openGraphicsComponentCore<NodeGraphicsComponent>(
      NodeGraphicsComponent,
      (_) => (this.nodeGraphicsComponent = _),
      (_) => (this.nodeGraphicsWindowInstance = _),
      () => (this.nodeGraphicsComponent.entity = entity),
      'NodeGraphics',
    );
  }
  private nodeGraphicsComponent: NodeGraphicsComponent = null;

  public async openLongitudinalSectionComponent(entities: DataEntity[]): Promise<void> {
    if (this.longitudinalSectionComponent) {
      this.longitudinalSectionComponent.entities = entities;
      return;
    }

    await this.openGraphicsComponentCore<LongitudinalSectionComponent>(
      LongitudinalSectionComponent,
      (_) => (this.longitudinalSectionComponent = _),
      (_) => (this.longitudinalSectionWindowInstance = _),
      () => (this.longitudinalSectionComponent.entities = entities),
      'LongitudinalSection',
    );
  }
  private longitudinalSectionComponent: LongitudinalSectionComponent = null;

  private async openGraphicsComponentCore<TGraphicsComponent>(
    graphicsComponentConstructor: new (...args: any[]) => TGraphicsComponent,
    setGraphicsComponent: (_: TGraphicsComponent) => void,
    setWindowInstance: (_: AbcWindowComponent) => void,
    setGraphicsComponentData: () => void,
    identifier: string,
  ): Promise<void> {
    const minSize = 100;
    const widthIdentifier = identifier + 'ComponentWidth';
    const heightIdentifier = identifier + 'ComponentHeight';
    let width = parseInt(localStorage.getItem(widthIdentifier));
    let height = parseInt(localStorage.getItem(heightIdentifier));

    const screenWidth = document.body.offsetWidth;
    const screenHeight = document.body.offsetHeight;
    const isSmallRes = this.isSmallResolution();
    const isTabletRes = !isSmallRes && screenWidth <= 1024;

    if (!(width > minSize) || !(height > minSize)) {
      width = isSmallRes || isTabletRes ? screenWidth - 20 : 900;
      height = isSmallRes || isTabletRes ? screenHeight - 20 : 700;
    } else {
      if (width > screenWidth) {
        width = screenWidth - 20;
      }
      if (height > screenHeight) {
        height = screenHeight - 20;
      }
    }

    const minWidth = 500;
    const style: WindowStyle = {
      height: height + 'px',
      width: width + 'px',
      resizable: true,
      minWidth: (isSmallRes || isTabletRes ? 300 : minWidth) + 'px',
      shading: false,
    };

    const windowAttr: IWindow = {
      id: identifier,
      label: (await this.resAsync).get(identifier + '-WindowTitle'),
      maximizeButton: true,
      minimizeButton: true,
      component: graphicsComponentConstructor,
      isInMobile: isSmallRes || isTabletRes,
      setWindowStyle: (windowStyle) => style,
    };

    let abcWindow: ViewContainerRef;
    const window = this.abcContainer.createComponent(abcWindow, AbcWindowComponent, windowAttr);
    const windowInstance: AbcWindowComponent = <AbcWindowComponent>window.componentRef.instance;
    setWindowInstance(windowInstance);
    windowInstance.createComponentInstance().subscribe((windowComponent: any) => {
      const graphicsComponentInstance: TGraphicsComponent = <TGraphicsComponent>windowComponent.openWindow().instance;
      setGraphicsComponent(graphicsComponentInstance);
      setGraphicsComponentData();

      windowComponent.onContentReady.subscribe((event) => {
        const contentElement = event.component.content();
        contentElement.style.overflow = 'hidden';
      });
      windowComponent.onClosing.subscribe(() => {
        width = windowComponent?.sizeParameter?.width;
        height = windowComponent?.sizeParameter?.height;

        if (width > minSize && height > minSize) {
          localStorage.setItem(widthIdentifier, width.toString());
          localStorage.setItem(heightIdentifier, height.toString());
        }
      });
      windowComponent.onAfterClosed.subscribe(() => {
        window.componentRef.destroy();
        setGraphicsComponent(null);
        setWindowInstance(null);
      });
    });
  }

  public async showM150ExportComponent(): Promise<void> {
    this.showInterfaceWindowCore(
      'm150Export',
      (await this.resAsync).get('M150Export-WindowTitle'),
      M150ExportComponent,
      (windowsInstance) => this.m150ExportService.loadWizard(this.getExportSpatialFilter(), windowsInstance),
    );
  }

  public async showGisExportComponent(): Promise<void> {
    this.showInterfaceWindowCore(
      'gisExport',
      (await this.resAsync).get('GisExport-WindowTitle'),
      GisExportComponent,
      async (windowsInstance) =>
        this.gisExportService.loadWizard(
          this.getExportSpatialFilter(),
          this.getParameters(),
          windowsInstance,
          await this.getActiveLayers(),
        ),
    );
  }

  //#Shape
  public async showShapeImportComponent(): Promise<void> {
    this.showInterfaceWindowCore(
      'ShapeImport',
      (await this.resAsync).get('ShapeImport-WindowTitle'),
      AsciiImportComponent,
      () => this.asciiImportService.loadWizard(),
      true,
    );
  }

  private wizardImportComponfent: WizardImportComponent;
  private showInterfaceWindowCore(
    id: string,
    label: string,
    component: Type<unknown>,
    initCall: (windowInstance: AbcWindowComponent) => void,
    isImport: boolean = false,
  ): void {
    const style: WindowStyle = {
      height: '55%',
      width: '40%',
      minHeight: '45%',
      minWidth: '35%',
      resizable: true,
      shading: true,
    };
    const windowAttr: IWindow = {
      id: id,
      label: label,
      maximizeButton: true,
      minimizeButton: true,
      component: component,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };

    let abcWindow: ViewContainerRef;
    const window = this.abcContainer.createComponent(abcWindow, AbcWindowComponent, windowAttr);
    const windowInstance: AbcWindowComponent = <AbcWindowComponent>window.componentRef.instance;

    windowInstance.createComponentInstance().subscribe((windowComponent: any) => {
      const w = windowComponent.openWindow();
      if (isImport) {
        this.wizardImportComponfent = w.instance.wizard;
        this.wizardImportComponfent.wizardCloseClick.subscribe(() => window.componentRef.destroy());
      }

      initCall(windowInstance);
      windowComponent.onAfterClosed.subscribe(() => {
        window.componentRef.destroy();
      });
    });
  }

  private getExportSpatialFilter(): number[][] {
    const ext = this.mapService.getCurrentView().getCurrentExtentWithDataProjection();
    return [
      [ext[0], ext[1]],
      [ext[2], ext[1]],
      [ext[2], ext[3]],
      [ext[0], ext[3]],
      [ext[0], ext[1]],
    ];
  }

  private getParameters(): ParameterValue[] {
    let parameters: ParameterValue[] = [];
    if (this.mapService.getCurrentQueryParameters) {
      parameters = this.mapService.getCurrentQueryParameters();
    }

    return parameters;
  }

  private async getActiveLayers(): Promise<string[]> {
    return await this.mapService.getVisibleSimpleLayerNames();
  }
  private confirmCachingClosing(): Observable<DialogResult> {
    return from(this.resAsync).pipe(
      map((resset) => {
        const settings = new MessageSettings();
        settings.mode = MessageMode.Question;
        // settings.icon = MessageBoxIcon.Info;
        // settings.button = MessageBoxButtons.OK;
        settings.message = resset.get('CacheConfig-ClosingMessage');
        return settings;
      }),
      concatMap((settings) => {
        return this.messageBoxService.showMessageBox(settings);
      }),
    );
  }

  showDocuments(objectInfos: DataEntity[], clickSource?: boolean) {
    const sub = this.onViewStateChanged().subscribe((state) => {
      if (
        state.type === ViewStateChangeArgType.BottomSheetPanelOpening ||
        state.type === ViewStateChangeArgType.BottomSheetPanelOpened
      ) {
        this.sendDataForDocuments(objectInfos, clickSource);
        sub.unsubscribe();
      }
    });
    this.openBottomSheet(this.frame.getWidget(BasysMapFrameService.Widget_Documents));
  }

  onBottomSheetCloseDemand() {
    return this.closeBottomSheetSubject.asObservable();
  }
  onBottomSheetOpenDemand() {
    return this.openBottomSheetSubject.asObservable();
  }

  sendDataForObjectInfo(objectInfos: DataEntity[]) {
    this.objectInfoDataSubject.next(objectInfos);
  }

  getObjectInfoData(): Observable<DataEntity[]> {
    return this.objectInfoDataSubject.asObservable();
  }

  sendDataForDocuments(objectInfos: DataEntity[], clickSource?: boolean) {
    this.isClickFromTableSubject.next(clickSource);
    this.documentsDataSubject.next(objectInfos);
  }

  getDocumentsData(): Observable<DataEntity[]> {
    return this.documentsDataSubject.asObservable();
  }

  sendDataForAddObject(objectInfo: DataEntity) {
    this.addObjectDataSubject.next(objectInfo);
  }

  getAddObjectData(): Observable<DataEntity> {
    return this.addObjectDataSubject.asObservable();
  }

  raiseViewStateChanged(data: ViewStateChangeArg) {
    if (data.bottomSheetPanelHeight) {
      this.currentBottomSheetPanelHeight = data.bottomSheetPanelHeight;
      this.reloadGrid.next(true);
    }
    this.viewStateChangedSubject.next(data);
  }

  onViewStateChanged(): Observable<ViewStateChangeArg> {
    return this.viewStateChangedSubject.asObservable();
  }

  getShowDocumentClickSource() {
    return this.isClickFromTableSubject.asObservable();
  }
}
